import { useState, useEffect } from "react";
import { getBillingData } from "./api";

/* ─────────────────────────────────────────────────────────
   STYLES  (scoped to .apc- prefix to avoid collisions)
───────────────────────────────────────────────────────── */
const css = `
  @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800&family=JetBrains+Mono:wght@400;500&display=swap');

  .apc-page  { padding:24px; font-family:'Outfit',sans-serif; }
  .apc-header { display:flex; align-items:center; justify-content:space-between; flex-wrap:wrap; gap:12px; margin-bottom:24px; }
  .apc-title  { font-size:20px; font-weight:800; color:#eef4ff; letter-spacing:-0.02em; }
  .apc-sub    { font-size:12px; color:#5a7a99; margin-top:3px; }
  .apc-controls { display:flex; align-items:center; gap:10px; flex-wrap:wrap; }
  .apc-month-sel { background:rgba(59,158,255,0.05); border:1px solid rgba(59,158,255,0.2); border-radius:10px; color:#eef4ff; font-family:'Outfit',sans-serif; font-size:13px; padding:8px 14px; outline:none; cursor:pointer; }
  .apc-month-sel option { background:#0c1628; }
  .apc-refresh { display:inline-flex; align-items:center; gap:6px; padding:8px 14px; background:rgba(59,158,255,0.08); border:1px solid rgba(59,158,255,0.2); border-radius:9px; color:#3b9eff; font-size:12px; font-family:'Outfit',sans-serif; cursor:pointer; transition:all 0.18s; }
  .apc-refresh:hover { background:rgba(59,158,255,0.15); }

  /* Grand total cards */
  .apc-grand { display:grid; grid-template-columns:repeat(4,1fr); gap:12px; margin-bottom:24px; }
  .apc-grand-card { background:#0c1628; border:1px solid rgba(255,255,255,0.07); border-radius:14px; padding:18px 20px; animation:apcUp 0.4s ease both; }
  .apc-grand-card:nth-child(1){animation-delay:.04s}.apc-grand-card:nth-child(2){animation-delay:.08s}
  .apc-grand-card:nth-child(3){animation-delay:.12s}.apc-grand-card:nth-child(4){animation-delay:.16s}
  .apc-card-icon { font-size:20px; margin-bottom:6px; }
  .apc-card-lbl  { font-size:9px; font-weight:600; color:#5a7a99; letter-spacing:0.09em; text-transform:uppercase; margin-bottom:6px; }
  .apc-card-val  { font-family:'JetBrains Mono',monospace; font-size:26px; font-weight:700; }
  .apc-card-sub  { font-size:10px; color:#5a7a99; margin-top:4px; }
  @keyframes apcUp { from{opacity:0;transform:translateY(12px)} to{opacity:1;transform:translateY(0)} }

  /* Client sections */
  .apc-company      { background:#080f22; border:1px solid rgba(255,255,255,0.07); border-radius:16px; overflow:hidden; margin-bottom:14px; animation:apcUp 0.5s ease 0.2s both; }
  .apc-company-head { display:flex; align-items:center; justify-content:space-between; padding:14px 20px; background:rgba(59,158,255,0.04); border-bottom:1px solid rgba(255,255,255,0.06); cursor:pointer; user-select:none; flex-wrap:wrap; gap:10px; }
  .apc-company-name { font-size:14px; font-weight:700; color:#eef4ff; display:flex; align-items:center; gap:8px; }
  .apc-company-meta { display:flex; align-items:center; gap:14px; flex-wrap:wrap; }
  .apc-status-badge { font-size:10px; font-weight:600; padding:3px 9px; border-radius:20px; }
  .apc-status-active   { background:rgba(34,211,168,0.1); color:#22d3a8; border:1px solid rgba(34,211,168,0.2); }
  .apc-status-inactive { background:rgba(255,107,138,0.1); color:#ff6b8a; border:1px solid rgba(255,107,138,0.2); }
  .apc-company-total { font-family:'JetBrains Mono',monospace; font-size:15px; font-weight:700; color:#60b8ff; }
  .apc-chevron { color:#5a7a99; font-size:14px; transition:transform 0.2s; line-height:1; }
  .apc-chevron.open { transform:rotate(90deg); color:#3b9eff; }

  /* User table */
  .apc-table    { width:100%; border-collapse:collapse; font-size:12.5px; }
  .apc-table th { padding:10px 18px; font-size:9.5px; font-weight:600; color:#5a7a99; letter-spacing:0.08em; text-transform:uppercase; border-bottom:1px solid rgba(255,255,255,0.06); text-align:left; }
  .apc-table td { padding:11px 18px; border-bottom:1px solid rgba(255,255,255,0.04); color:#8aabb8; vertical-align:middle; }
  .apc-table tr:last-child td { border-bottom:none; }
  .apc-table tr:hover td { background:rgba(59,158,255,0.04); color:#eef4ff; }
  .apc-user-name  { font-weight:600; color:#eef4ff; }
  .apc-paused     { color:#ff6b8a; font-size:10px; font-weight:600; margin-left:6px; }
  .apc-mono       { font-family:'JetBrains Mono',monospace; }
  .apc-total-row td { background:rgba(59,158,255,0.05); color:#eef4ff; font-weight:700; border-top:1px solid rgba(59,158,255,0.15); }
  .apc-no-activity { color:#5a7a99; font-size:11px; font-style:italic; }

  /* Source split mini-bar */
  .apc-minibar     { display:flex; height:7px; border-radius:4px; overflow:hidden; gap:2px; min-width:60px; }
  .apc-minibar-seg { height:100%; border-radius:2px; }

  /* Empty / loading */
  .apc-empty       { padding:60px 20px; text-align:center; color:#5a7a99; }
  .apc-empty-icon  { font-size:40px; margin-bottom:12px; opacity:0.4; }
  .apc-loading     { display:flex; align-items:center; justify-content:center; padding:80px; color:#5a7a99; gap:10px; font-size:14px; }

  /* Grand footer */
  .apc-footer { background:#080f22; border:1px solid rgba(59,158,255,0.2); border-radius:14px; padding:16px 22px; margin-top:8px; animation:apcUp 0.5s ease 0.3s both; display:flex; align-items:center; justify-content:space-between; flex-wrap:wrap; gap:14px; }

  @media(max-width:900px){
    .apc-grand { grid-template-columns:repeat(2,1fr); }
    .apc-table th:nth-child(5),.apc-table td:nth-child(5){ display:none; }
  }
  @media(max-width:580px){
    .apc-grand { grid-template-columns:repeat(2,1fr); }
    .apc-page  { padding:14px; }
  }
`;

/* ─────────────────────────────────────────────────────────
   COMPONENT
───────────────────────────────────────────────────────── */
export default function AdminPageCount() {
  const [data,     setData]     = useState(null);
  const [month,    setMonth]    = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState("");
  const [expanded, setExpanded] = useState({});
  const [months,   setMonths]   = useState([]);

  const load = async (m, silent = false) => {
    if (!silent) { setLoading(true); setError(""); }
    try {
      const d = await getBillingData(m || null);
      setData(d);
      if (!months.length) {
        const cur = d.month || new Date().toISOString().slice(0, 7);
        const list = [];
        const [y, mo] = cur.split("-").map(Number);
        for (let i = 0; i < 12; i++) {
          let mm = mo - i; let yy = y;
          if (mm < 1) { mm += 12; yy -= 1; }
          list.push(`${yy}-${String(mm).padStart(2, "0")}`);
        }
        setMonths(list);
      }
    } catch (e) {
      if (!silent) setError(e.message || "Failed to load page count data");
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => { load(month); }, [month]);

  // Quietly re-poll every 15 s so new activity shows up without a manual refresh
  useEffect(() => {
    const t = setInterval(() => load(month, true), 15000);
    return () => clearInterval(t);
  }, [month]);

  const toggle = id => setExpanded(p => ({ ...p, [id]: !p[id] }));

  /* ── Loading / Error states ── */
  if (loading && !data) return (
    <>
      <style>{css}</style>
      <div className="apc-loading">
        <span style={{ display: "inline-block", animation: "apcUp 1s linear infinite" }}>↻</span>
        Loading page count data…
      </div>
    </>
  );

  if (error) return (
    <>
      <style>{css}</style>
      <div className="apc-empty">
        <div className="apc-empty-icon">⚠️</div>
        {error}
      </div>
    </>
  );

  /* ── Derived totals ── */
  const clients      = data?.clients || [];
  const curMonth     = data?.month || month || "";
  const grandPages   = data?.grand_pages || 0;
  const grandPurch   = clients.reduce((s, c) => s + c.users.reduce((u, r) => u + (r.purchase_pages || 0), 0), 0);
  const grandSales   = clients.reduce((s, c) => s + c.users.reduce((u, r) => u + (r.sales_pages    || 0), 0), 0);
  const grandBank    = clients.reduce((s, c) => s + c.users.reduce((u, r) => u + (r.bank_pages     || 0), 0), 0);
  const grandUsers   = clients.reduce((s, c) => s + c.users.length, 0);

  return (
    <>
      <style>{css}</style>
      <div className="apc-page">

        {/* ── Header ── */}
        <div className="apc-header">
          <div>
            <div className="apc-title">📋 Page Count</div>
            <div className="apc-sub">Pages processed per user, split by document type · updates every 15 s</div>
          </div>
          <div className="apc-controls">
            <select
              className="apc-month-sel"
              value={month || curMonth}
              onChange={e => setMonth(e.target.value)}
            >
              {months.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
            <button className="apc-refresh" onClick={() => load(month || curMonth)}>
              <span style={{ display: "inline-block", animation: loading ? "apcUp 1s linear infinite" : "none" }}>↻</span>
              {loading ? "Loading…" : "Refresh"}
            </button>
          </div>
        </div>

        {/* ── Summary cards ── */}
        <div className="apc-grand">
          {[
            { icon: "📊", label: "Total Pages",     val: grandPages, color: "#60b8ff", sub: curMonth },
            { icon: "📄", label: "Purchase Pages",  val: grandPurch, color: "#3b9eff", sub: "Invoice extractions" },
            { icon: "🛒", label: "Sales Pages",     val: grandSales, color: "#22d3a8", sub: "Sales extractions"   },
            { icon: "🏦", label: "Bank Pages",      val: grandBank,  color: "#0ea5e9", sub: "Statement pages"     },
          ].map((c, i) => (
            <div key={i} className="apc-grand-card">
              <div className="apc-card-icon">{c.icon}</div>
              <div className="apc-card-lbl">{c.label}</div>
              <div className="apc-card-val" style={{ color: c.color }}>{c.val.toLocaleString()}</div>
              <div className="apc-card-sub">{c.sub}</div>
            </div>
          ))}
        </div>

        {/* ── Empty state ── */}
        {clients.length === 0 && (
          <div className="apc-empty">
            <div className="apc-empty-icon">🏢</div>
            No activity recorded for {curMonth}.
          </div>
        )}

        {/* ── Per-client sections ── */}
        {clients.map(co => {
          const open    = expanded[co.id] !== false; // expanded by default
          const tp      = co.total_pages || 0;
          const coPurch = co.users.reduce((s, u) => s + (u.purchase_pages || 0), 0);
          const coSales = co.users.reduce((s, u) => s + (u.sales_pages    || 0), 0);
          const coBank  = co.users.reduce((s, u) => s + (u.bank_pages     || 0), 0);

          return (
            <div key={co.id} className="apc-company">

              {/* Client header */}
              <div className="apc-company-head" onClick={() => toggle(co.id)}>
                <div className="apc-company-name">
                  🏢 {co.name}
                  <span className={`apc-status-badge ${co.status === "Active" ? "apc-status-active" : "apc-status-inactive"}`}>
                    {co.status}
                  </span>
                </div>
                <div className="apc-company-meta">
                  <span style={{ fontSize: 12, color: "#8aabb8" }}>
                    <span style={{ color: "#3b9eff" }}>{coPurch}</span>
                    {" / "}
                    <span style={{ color: "#22d3a8" }}>{coSales}</span>
                    {" / "}
                    <span style={{ color: "#0ea5e9" }}>{coBank}</span>
                    <span style={{ fontSize: 10, color: "#5a7a99" }}> (P / S / B)</span>
                  </span>
                  <span className="apc-company-total">{tp.toLocaleString()} pages</span>
                  <span className={`apc-chevron ${open ? "open" : ""}`}>›</span>
                </div>
              </div>

              {/* User table */}
              {open && (
                <div style={{ overflowX: "auto" }}>
                  <table className="apc-table">
                    <thead>
                      <tr>
                        <th>User</th>
                        <th style={{ color: "#3b9eff" }}>📄 Purchase Pages</th>
                        <th style={{ color: "#22d3a8" }}>🛒 Sales Pages</th>
                        <th style={{ color: "#0ea5e9" }}>🏦 Bank Pages</th>
                        <th>Total Pages</th>
                        <th>Source Split</th>
                      </tr>
                    </thead>
                    <tbody>
                      {co.users.length === 0 && (
                        <tr>
                          <td colSpan={6} style={{ textAlign: "center", color: "#5a7a99", padding: "20px" }}>
                            No users for this company this month.
                          </td>
                        </tr>
                      )}

                      {co.users.map(u => {
                        const total = u.pages || 0;
                        const pp    = u.purchase_pages || 0;
                        const sp    = u.sales_pages    || 0;
                        const bp    = u.bank_pages     || 0;
                        const barW  = Math.max(50, Math.min(120, total * 2));
                        return (
                          <tr key={u.user_id}>
                            <td>
                              <span className="apc-user-name">{u.username}</span>
                              {!u.key_active && <span className="apc-paused">⏸ PAUSED</span>}
                            </td>
                            <td className="apc-mono" style={{ color: "#3b9eff" }}>
                              {pp > 0 ? pp.toLocaleString() : <span className="apc-no-activity">—</span>}
                            </td>
                            <td className="apc-mono" style={{ color: "#22d3a8" }}>
                              {sp > 0 ? sp.toLocaleString() : <span className="apc-no-activity">—</span>}
                            </td>
                            <td className="apc-mono" style={{ color: "#0ea5e9" }}>
                              {bp > 0 ? bp.toLocaleString() : <span className="apc-no-activity">—</span>}
                            </td>
                            <td className="apc-mono" style={{ color: "#eef4ff", fontWeight: 700 }}>
                              {total.toLocaleString()}
                            </td>
                            <td>
                              {total > 0 ? (
                                <div className="apc-minibar" style={{ width: barW + "px" }}>
                                  {pp > 0 && <div className="apc-minibar-seg" style={{ background: "#3b9eff", width: `${(pp / total) * 100}%` }} />}
                                  {sp > 0 && <div className="apc-minibar-seg" style={{ background: "#22d3a8", width: `${(sp / total) * 100}%` }} />}
                                  {bp > 0 && <div className="apc-minibar-seg" style={{ background: "#0ea5e9", width: `${(bp / total) * 100}%` }} />}
                                </div>
                              ) : (
                                <span className="apc-no-activity">no activity</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}

                      {/* Company subtotal — only if more than one user */}
                      {co.users.length > 1 && (
                        <tr className="apc-total-row">
                          <td>Company Total</td>
                          <td className="apc-mono" style={{ color: "#3b9eff" }}>{coPurch.toLocaleString()}</td>
                          <td className="apc-mono" style={{ color: "#22d3a8" }}>{coSales.toLocaleString()}</td>
                          <td className="apc-mono" style={{ color: "#0ea5e9" }}>{coBank.toLocaleString()}</td>
                          <td className="apc-mono" style={{ color: "#60b8ff" }}>{tp.toLocaleString()}</td>
                          <td>
                            <div className="apc-minibar" style={{ width: "100px" }}>
                              {coPurch > 0 && <div className="apc-minibar-seg" style={{ background: "#3b9eff", width: `${(coPurch / tp) * 100}%` }} />}
                              {coSales > 0 && <div className="apc-minibar-seg" style={{ background: "#22d3a8", width: `${(coSales / tp) * 100}%` }} />}
                              {coBank  > 0 && <div className="apc-minibar-seg" style={{ background: "#0ea5e9", width: `${(coBank  / tp) * 100}%` }} />}
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })}

        {/* ── Grand footer ── */}
        {clients.length > 0 && (
          <div className="apc-footer">
            <span style={{ fontSize: 14, fontWeight: 700, color: "#eef4ff" }}>
              Grand Total · {curMonth}
            </span>
            <div style={{ display: "flex", gap: 28, flexWrap: "wrap" }}>
              {[
                ["Active Users",    grandUsers,  "#a78bfa"],
                ["📄 Purchase",     grandPurch,  "#3b9eff"],
                ["🛒 Sales",        grandSales,  "#22d3a8"],
                ["🏦 Bank",         grandBank,   "#0ea5e9"],
                ["📊 Total Pages",  grandPages,  "#60b8ff"],
              ].map(([l, v, c]) => (
                <div key={l} style={{ textAlign: "center" }}>
                  <div style={{ fontSize: 9, color: "#5a7a99", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 3 }}>{l}</div>
                  <div style={{ fontFamily: "'JetBrains Mono',monospace", fontWeight: 700, color: c, fontSize: 15 }}>{typeof v === "number" ? v.toLocaleString() : v}</div>
                </div>
              ))}
            </div>
          </div>
        )}

      </div>
    </>
  );
}
